/**
 * canvasGeoProjection — pure, React-free snapshot projection mapping
 * React Flow canvas positions (x, y in canvas pixels) <-> WGS84
 * {lat, lng}.
 *
 * Feature #91 strategy decision (OPTION (b) — fixed anchor + scale)
 * ----------------------------------------------------------------
 * `MapBackgroundLayer.tsx` is decorative by explicit design: it pans /
 * zooms via a CSS transform while Google Maps owns its own tile
 * lifecycle, so a per-frame RF<->Mercator sync is out of scope and
 * fragile (see the comment block in MapBackgroundLayer.tsx). Rather
 * than chase a live projection sync, we capture a ONE-TIME snapshot at
 * the moment geo is enabled:
 *
 *   - anchor          = site.location {lat, lng} (the canvas origin
 *                       reference point in geo space).
 *   - metersPerPixel  = ground distance one canvas pixel represents,
 *                       derived from a chosen map zoom (Web Mercator
 *                       ground resolution at the site latitude).
 *
 * A canvas position (x, y) is then mapped to a local ENU (East /
 * North) offset in metres:
 *
 *   east  = (x - anchorPx.x) * metersPerPixel
 *   north = (anchorPx.y - y) * metersPerPixel   // canvas Y grows DOWN,
 *                                               //   North grows UP
 *
 * and that ENU offset is converted to {lat, lng} via the small-distance
 * inverse-haversine (equirectangular) approximation:
 *
 *   lat = anchor.lat + (north / M_PER_DEG_LAT)
 *   lng = anchor.lng + (east  / (M_PER_DEG_LAT * cos(anchor.lat)))
 *
 * The inverse (toPosition) is the exact algebraic inverse.
 *
 * Units
 * -----
 *   - position {x, y}: canvas pixels (React Flow node.position).
 *   - {lat, lng}:      WGS84 degrees.
 *   - metersPerPixel:  metres of ground per canvas pixel.
 *
 * Accuracy
 * --------
 * The equirectangular ENU approximation is sub-metre accurate at the
 * parcel scale (≤ a few km from the anchor); it deteriorates with
 * distance and near the poles, but a sim canvas spans a single site so
 * the offsets stay small. We deliberately reuse the same
 * `M_PER_DEG_LAT ≈ 111320` convention as `sampleGrid.ts` /
 * `siteLayersBlock.ts` so the geo helpers across the codebase agree on
 * a single "metres per degree" definition.
 *
 * This module is pure (no React, no I/O, no Zustand) and trivially
 * unit-testable.
 */

import type { LatLng } from '../../../geometry/sampleGrid';

/**
 * Metres per degree of latitude. Project-wide convention shared with
 * `sampleGrid.ts` (`bufferedBboxGrid`) and `siteLayersBlock.ts`. 1 deg
 * of latitude ≈ 111320 m (WGS84 mean).
 */
export const M_PER_DEG_LAT = 111320;

/** Degrees -> radians. */
const DEG_TO_RAD = Math.PI / 180;

/**
 * Earth circumference at the equator (m), used for the Web Mercator
 * ground-resolution formula. WGS84 equatorial radius 6378137 m.
 */
const EARTH_EQUATORIAL_CIRCUMFERENCE_M = 2 * Math.PI * 6378137;

/** Web Mercator base tile size in pixels (Google/Leaflet convention). */
const MERCATOR_TILE_SIZE_PX = 256;

/**
 * Web Mercator ground resolution (metres per pixel) at a given latitude
 * and zoom level. Standard slippy-map formula:
 *
 *   metresPerPixel = (cos(lat) * earthCircumference) / (tileSize * 2^zoom)
 *
 * At zoom 0 the whole world is one 256px tile; each zoom level halves
 * the ground distance per pixel. This is the canonical relationship
 * Google Maps / Leaflet use, so the snapshot scale matches what a user
 * sees on the decorative map background at the chosen zoom.
 *
 * @param lat   Latitude in degrees (the site anchor latitude).
 * @param zoom  Map zoom level (Google Maps convention; site.zoom).
 */
export function metersPerPixelAtZoom(lat: number, zoom: number): number {
  const cosLat = Math.cos(lat * DEG_TO_RAD);
  return (
    (cosLat * EARTH_EQUATORIAL_CIRCUMFERENCE_M) /
    (MERCATOR_TILE_SIZE_PX * Math.pow(2, zoom))
  );
}

/**
 * A captured snapshot projection. Maps canvas positions <-> {lat, lng}
 * about a fixed anchor and scale (see module docstring).
 */
export interface CanvasGeoProjection {
  /** Geo anchor — the canvas origin reference point. */
  readonly anchor: LatLng;
  /** Ground metres per canvas pixel at the anchor latitude. */
  readonly metersPerPixel: number;
  /** Canvas pixel position that maps exactly to the anchor. */
  readonly anchorPosition: { x: number; y: number };
  /** Map canvas position (x, y) -> {lat, lng}. */
  toLatLng(pos: { x: number; y: number }): LatLng;
  /** Map {lat, lng} -> canvas position (x, y). Inverse of toLatLng. */
  toPosition(p: LatLng): { x: number; y: number };
}

/**
 * Parameters for capturing a projection snapshot.
 */
export interface CanvasGeoProjectionParams {
  /** Geo anchor — typically `site.location`. */
  anchor: LatLng;
  /**
   * Map zoom used to derive `metersPerPixel`. Typically `site.zoom`
   * (defaults to 17 — the parcel-scale zoom used by MapBackgroundLayer).
   */
  zoom?: number;
  /**
   * Direct `metersPerPixel` override. When provided, `zoom` is ignored.
   * Useful for tests that want a clean integer scale.
   */
  metersPerPixel?: number;
  /**
   * Canvas pixel position that corresponds to the anchor. Defaults to
   * the canvas origin {x: 0, y: 0}. Pass the canvas centre (or a
   * reference node's position) to anchor geo there instead.
   */
  anchorPosition?: { x: number; y: number };
}

/** Default parcel-scale zoom — matches MapBackgroundLayer's default. */
export const DEFAULT_GEO_ZOOM = 17;

/**
 * Capture a fixed anchor + scale projection snapshot.
 *
 * The returned object is frozen-ish (its methods close over the
 * captured anchor / scale) so callers can hold it for the duration of a
 * geo-enabled session without it drifting.
 */
export function captureCanvasGeoProjection(
  params: CanvasGeoProjectionParams,
): CanvasGeoProjection {
  const anchor = params.anchor;
  const zoom = params.zoom ?? DEFAULT_GEO_ZOOM;
  const metersPerPixel =
    params.metersPerPixel ?? metersPerPixelAtZoom(anchor.lat, zoom);
  const anchorPosition = params.anchorPosition ?? { x: 0, y: 0 };
  const cosLat = Math.cos(anchor.lat * DEG_TO_RAD);
  // Guard against a degenerate cos(lat) near the poles so the longitude
  // scaling never divides by ~0 (sub-metre site canvas — irrelevant in
  // practice, but keeps the inverse total).
  const safeCosLat = Math.abs(cosLat) < 1e-6 ? 1e-6 : cosLat;

  return {
    anchor,
    metersPerPixel,
    anchorPosition,
    toLatLng(pos: { x: number; y: number }): LatLng {
      // Canvas Y grows DOWNWARD; ENU North grows UPWARD — flip sign.
      const east = (pos.x - anchorPosition.x) * metersPerPixel;
      const north = (anchorPosition.y - pos.y) * metersPerPixel;
      const lat = anchor.lat + north / M_PER_DEG_LAT;
      const lng = anchor.lng + east / (M_PER_DEG_LAT * safeCosLat);
      return { lat, lng };
    },
    toPosition(p: LatLng): { x: number; y: number } {
      const north = (p.lat - anchor.lat) * M_PER_DEG_LAT;
      const east = (p.lng - anchor.lng) * M_PER_DEG_LAT * safeCosLat;
      const x = anchorPosition.x + east / metersPerPixel;
      const y = anchorPosition.y - north / metersPerPixel;
      return { x, y };
    },
  };
}
